// iter715 Phase C — Rich match-candidates picker. Replaces the blind
// auto-match best-pick with a side-by-side list of up to 10 ranked
// suggestions, each showing party / branch / ref / amount-delta /
// date-delta / score / "why" explanation. Used from BankStatementsPage
// when a transaction is ambiguous (multiple bills at the same amount).
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, ArrowUpRight, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { CardSettlementPanel } from '@/components/banking/CardSettlementPanel';

const TYPE_LABEL = {
  sale: 'Sale',
  expense: 'Expense',
  supplier_payment: 'Supplier payment',
  bank_payment: 'Bank payment',
};
const TYPE_TONE = {
  sale: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  expense: 'bg-rose-50 border-rose-200 text-rose-700',
  supplier_payment: 'bg-orange-50 border-orange-200 text-orange-700',
  bank_payment: 'bg-sky-50 border-sky-200 text-sky-700',
};

export default function MatchCandidatesDialog({ open, onOpenChange, statementId, txnIdx, onMatched }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [picking, setPicking] = useState(null); // candidate.id while linking

  useEffect(() => {
    if (!open || !statementId || txnIdx == null) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/bank-statements/${statementId}/txn-candidates?txn_idx=${txnIdx}&top=10`);
        if (!cancelled) setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load candidates');
        if (!cancelled) onOpenChange(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, statementId, txnIdx, onOpenChange]);

  const pickMatch = async (c) => {
    setPicking(c.id);
    try {
      await api.post(`/bank-statements/${statementId}/manual-match`, {
        txn_index: txnIdx,
        match_type: c.type,
        match_id: c.id,
      });
      toast.success(`Linked to ${TYPE_LABEL[c.type] || c.type} — ${c.party_name || ''}`);
      onMatched?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Match failed');
    } finally {
      setPicking(null);
    }
  };

  const txn = data?.txn;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto" data-testid="match-candidates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-violet-600" /> Find a match
          </DialogTitle>
          <DialogDescription>
            Pick the system entry this bank transaction settles. Top results consider amount, date, party name, branch and reference number — not just amount.
          </DialogDescription>
        </DialogHeader>

        {txn && (
          <div className="rounded-xl border border-border bg-muted/40 p-3 text-sm" data-testid="match-txn-summary">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="font-semibold text-foreground">{txn.description || '(no description)'}</p>
                <p className="text-xs text-muted-foreground">
                  {txn.date}{txn.beneficiary ? ` · to/from ${txn.beneficiary}` : ''}{txn.reference ? ` · ref ${txn.reference}` : ''}
                </p>
              </div>
              <div className={`text-base font-mono font-bold ${txn.type === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>
                {txn.type === 'credit' ? '↑ ' : '↓ '}{Number(txn.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        )}

        {txn?.type === 'credit' && !loading && (
          <CardSettlementPanel statementId={statementId} txnIdx={txnIdx} deposit={txn.amount}
            onDone={() => { onMatched?.(); onOpenChange(false); }} />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading candidates…
          </div>
        ) : (data?.candidates || []).length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="match-empty">
            No candidates found within 20% of this amount. Try recording the entry first, or use the manual-link form.
          </div>
        ) : (
          <div className="space-y-2 max-h-[55dvh] overflow-y-auto pr-1" data-testid="match-candidates-list">
            {(data.candidates || []).map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => pickMatch(c)}
                disabled={picking === c.id}
                className="w-full text-left rounded-xl border border-border hover:border-violet-300 hover:bg-violet-50/40 p-3 transition-colors disabled:opacity-50"
                data-testid={`match-candidate-${c.id}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wide font-bold border ${TYPE_TONE[c.type] || ''}`}>
                      {TYPE_LABEL[c.type] || c.type}
                    </Badge>
                    <span className="text-sm font-semibold truncate">{c.party_name || '—'}</span>
                    {c.branch && <span className="text-[11px] text-muted-foreground">· {c.branch}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold">
                      {Number(c.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    {Number(c.amt_diff) > 0.01 && (
                      <span className="text-[11px] text-amber-700 font-mono" title="Difference from bank amount">Δ {Number(c.amt_diff).toFixed(2)}</span>
                    )}
                    <Badge className={`text-[10px] ${c.score >= 90 ? 'bg-emerald-100 text-emerald-800' : c.score >= 65 ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-800'} border-0`}>
                      {Math.round(c.score)}% match
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                  <span className="truncate flex-1 min-w-0">
                    {c.date}{c.ref ? ` · ref ${c.ref}` : ''}{c.narration ? ` · ${c.narration}` : ''}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-violet-700/80 flex items-center gap-0.5 shrink-0">
                    {c.why} <ArrowUpRight size={10} />
                  </span>
                </div>
                {picking === c.id && (
                  <div className="mt-1 text-[11px] text-violet-700 flex items-center gap-1">
                    <Loader2 size={11} className="animate-spin" /> Linking…
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
